import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import {
  Button,
  Alert,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
  FormGroup,
} from 'reactstrap';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import LoginModal from '../../components/LoginModal';
import CarouselPage from '../AuthenticationInner/CarouselPage';
import LoginWithSocialNetwork from './LoginWithSocialNetwork';
import { setDocumentTitle, removeUnderscoreFromWord } from 'helpers';
import { useAuth } from 'hooks';
import logo from '../../assets/images/Swiftlee-logo-wide.png';
import { USER_TYPE_SUPER_ADMIN, USER_TYPE_ADMIN } from 'constants';

setDocumentTitle('Welcome Back');

const Login = ({ history }) => {
  const {
    clearMesages,
    error,
    loading,
    role,
    SwitchUserType,
    userType,
    login: { loginUser },
  } = useAuth();

  const isAdmin =
    userType === USER_TYPE_SUPER_ADMIN || userType === USER_TYPE_ADMIN;
  const roleName = removeUnderscoreFromWord(role);

  const schema = Yup.object({
    email: Yup.string().required('Email required'),
    password: Yup.string().required('Password required'),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async user => await loginUser({ ...user, userType }, history);

  return (
    <React.Fragment>
      <div>
        <Container fluid className="p-0">
          <Row className="g-0">
            <CarouselPage />
            {!isAdmin && <LoginModal />}
            <Col xl={3}>
              <div className="auth-full-page-content p-md-5 p-4">
                <div className="w-100">
                  <div className="d-flex flex-column h-100">
                    <div className="mb-4 mb-md-5">
                      <a
                        href="http://www.contactcsc.com/"
                        className="d-block auth-logo"
                      >
                        <img
                          src={logo}
                          alt="csc logo"
                          className="__login-logo"
                        />
                      </a>
                    </div>
                    <div className="my-auto">
                      <div>
                        <h5 className="text-primary">Welcome Back!</h5>
                        <h2>{roleName}</h2>
                        {!isAdmin && (
                          <p className="text-muted">
                            Sign in to continue to Swiftlee.
                          </p>
                        )}

                        {role && (
                          <SwitchUserType
                            title="Click here to switch"
                            role={roleName}
                          />
                        )}
                      </div>

                      {error && (
                        <Alert color="danger" toggle={clearMesages}>
                          {error}
                        </Alert>
                      )}

                      <div className="mt-4">
                        {isAdmin ? (
                          <Link to="/admin-login">Go to admin login</Link>
                        ) : (
                          <>
                            <Form
                              className="form-horizontal"
                              onSubmit={handleSubmit(onSubmit)}
                            >
                              <FormGroup>
                                <Label for="email">Email</Label>
                                <Controller
                                  name="email"
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      placeholder="Email"
                                      type="email"
                                      invalid={Boolean(errors.email)}
                                    />
                                  )}
                                />
                                {errors.email && (
                                  <FormFeedback>
                                    {errors.email?.message}
                                  </FormFeedback>
                                )}
                              </FormGroup>

                              <FormGroup>
                                <Label for="password">Password</Label>
                                <Controller
                                  name="password"
                                  control={control}
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      placeholder="Password"
                                      type="password"
                                      invalid={Boolean(errors.password)}
                                    />
                                  )}
                                />
                                {errors.password && (
                                  <FormFeedback>
                                    {errors.password?.message}
                                  </FormFeedback>
                                )}
                              </FormGroup>

                              <div className="mt-3 d-grid">
                                <Button color="primary">
                                  {loading ? (
                                    <React.Fragment>
                                      Loading...{' '}
                                      <Spinner size="sm">Loading...</Spinner>
                                    </React.Fragment>
                                  ) : (
                                    'Submit'
                                  )}
                                </Button>
                              </div>
                            </Form>

                            <LoginWithSocialNetwork />
                          </>
                        )}

                        <div className="mt-5 text-center">
                          <Link to="/forgot-password">Forgot password?</Link>
                          <p>
                            Don&apos;t have an account?{' '}
                            <Link
                              to="register"
                              className="fw-medium text-primary"
                            >
                              Sign up now
                            </Link>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Login);
